import { GCInstant as platform } from 'src/lib/gcinstant';
import { isSceneEntered } from 'src/lib/stateUtils';
import { PetSpeciesID, PetState } from 'src/replicant/pet';
import {
  ConversationalScript,
  DescriptiveScript,
  DialogData,
  DialogID,
  DialogScene,
  Script,
  ScriptID,
  SelectionalScript,
} from 'src/replicant/conversation/conservation.ruleset';
import * as Daycare from 'src/replicant/daycare';
import * as Duo from 'src/replicant/duo';
import { State } from 'src/state';
import StateObserver, { now } from 'src/StateObserver';
import PopupMonitor from '../logic/PopupMonitor';
import { getFriendsPet, getPet, isPetConfigured } from '../pet';
import { getTimesSeen } from 'src/replicant/conversation/conversation.getters';
import { petSpeciesAssets } from 'src/loadingGroups';
import { assertNever } from 'src/replicant/utils';
import { Scripts } from 'src/replicant/data/Scripts.schema';
import { Dialogs } from 'src/replicant/data/Dialogs.schema';
import * as Fate from 'src/replicant/fate';
import { getRandomBoolean } from 'src/replicant/utils/random';
import { duration } from 'src/replicant/utils/duration';
import { createPrng } from '@play-co/replicant';
import * as Tutorial from 'src/replicant/tutorial';
import { isReactivated } from 'src/replicant/resurrection/resurrection.getters';
import features from 'src/replicant/ruleset/features';
import { puzzleCoinsIsPlayingTutorial } from 'src/replicant/puzzlecoins/puzzlecoins.getters';
import { dreamDriftIsInstanceActive } from 'src/replicant/dreamDrift/dreamDrift.getters';
import { getPetConversationAssetPaths } from './conversation.assets';
import { dreamDriftIsPlaying } from 'src/game/dreamDrift/dreamDrift.utils';

/**
 * Checks if the auto dialog can be shown.
 * @param state
 * @returns
 */
export function canShowAutoDialog(state: State) {
  // Return false if auto dialog is blocked.
  if (getPet().conversation.isAutoDialogBlocked()) {
    return false;
  }

  const { care, user, pet } = state;
  const now = StateObserver.now();
  if (
    !pet[platform.playerID]?.species || // Should not trigger if species is not defined.
    care.currentCareAction || // Should not trigger if pet is being cared for.
    user.visit.visitSession || // Should not trigger while visiting.
    !isSceneEntered('livingRoom') || // Should not trigger while in other scenes.
    PopupMonitor.isAnyPopupOpen() || // Should not trigger while other popups are open.
    StateObserver.getState().user.match2.playing || // Should not trigger while playing match2.
    Duo.getters.getLastUnseenInvitation(user, now) || // Should not trigger if there is an unseen invitation.
    dreamDriftIsInstanceActive(user, now) || // Should not trigger during an active Dream Drift instance.
    Daycare.getters.isInDaycare(user, now) // Should not trigger if pet is in daycare.
  ) {
    return false;
  }

  // Should trigger if sleeping, evolving or had visitor.
  const petState = Fate.getters.getVisiblePetState(user, now);
  const { hadVisitor } = Fate.getters.getAllPermanentStatusEffects(user, now);
  return (
    petState === 'sleeping' ||
    petState === 'evolving' ||
    hadVisitor ||
    shouldShowPlayMatch2Dialog(state)
  );
}

/**
 * Check if should show match2 dialog.
 * @returns
 */
export function shouldShowPlayMatch2Dialog({ user }: State) {
  // Do not invite the player to Match2 while Dream Drift still owns the puzzle scene.
  if (dreamDriftIsPlaying()) return false;

  // Never show the match2 dialog while playing the bpoints-from-puzzle tutorial.
  if (puzzleCoinsIsPlayingTutorial(StateObserver.getState().user)) {
    return false;
  }

  // Return false if the pet is not configured.
  if (!isPetConfigured()) {
    return false;
  }

  // Return false if there are any open popups.
  if (PopupMonitor.isAnyPopupOpen()) {
    return false;
  }

  // Return false if the user is currently playing match2.
  if (user.match2.playing) {
    return false;
  }

  // Return false if the pet is not happy or normal.
  const allowedStates = ['happy', 'normal'];
  if (!allowedStates.includes(getPet().state)) {
    return false;
  }

  // Return false if user has not completed the match2 tutorial step yet.
  if (!Tutorial.getters.isTutorialStepComplete(user, 'match2')) {
    return false;
  }

  const timeSinceLastMatchGameH =
    (now() - user.match2.time) / duration({ hours: 1 });
  let chance = Math.min(100, (5 * timeSinceLastMatchGameH) / 2);

  // Resurrected users always get the dialog.
  if (isReactivated(user, now())) {
    chance = 100;
  }

  const rng = createPrng(`${Math.floor(now() / duration({ seconds: 1 }))}`);
  return getRandomBoolean(chance, rng);
}

export async function showAutoDialog() {
  // Should not show.
  const state = StateObserver.getState();
  if (!canShowAutoDialog(state)) return;

  // Show dialog.
  getPet().conversation.handlePetTapped('auto');
}

/**
 * Get player id from script id
 */
export function getPlayerIdFromConversation(conversationId: ScriptID) {
  if (getPet().conversation.getActiveScript() === conversationId) {
    return platform.playerID;
  }

  if (getFriendsPet()?.conversation.getActiveScript() === conversationId) {
    return getFriendsPet()?.id;
  }

  return platform.playerID;
}

/**
 * Subsitute tokens.
 * @param script
 * @param state
 * @param isFriend
 */
export function substituteScriptTokens(
  script: string,
  state: State['user'],
  isFriend: boolean,
) {
  // TODO Lookup substitutions based on tokens found in text
  const substitutions: Record<string, string | undefined> = {
    // Player's pet's name
    // Will be replaced with friend's pet's name when chatting with a friend
    pet_name: (isFriend && state.visit.visitSession?.petName) || state.pet.name,

    // Player's profile name
    player_name: state.publicProfile?.name ?? state.profile?.name ?? 'unknown',

    // Answers to any conversation questions given by the player
    ...Object.entries(state.conversation.answers).reduce(
      (acc, [answerVar, answer]) => {
        acc[answerVar] = answer.value;
        return acc;
      },
      {} as Record<string, string>,
    ),

    // Friend's display name
    'friend.player_name':
      state.visit.visitSession?.name ?? state.visit.lastVisitorName,

    // This refers to the player currently being visited (if any), otherwise
    // it refers to our most recent visitor (so our pet can tell us who visited)
    // If it's a friend, then it should refer to the current user's name instead
    'revivedBy.player_name': isFriend
      ? (state.publicProfile?.name ?? state.profile?.name ?? 'unknown')
      : (state.visit.visitSession?.name ??
        state.visit.revivedBy ??
        state.visit.lastVisitorName),

    // Duo
    'inviter.player_name': state.duo.lastReceivedInvitation.name,
    'invitee.player_name':
      state.duo.lastResponse?.name ?? state.visit.visitSession?.name,
    'invitee.message':
      state.duo.invitations.sent.find(
        ({ timestamp, playerId }) =>
          state.duo.lastResponse?.timestamp === timestamp &&
          state.duo.lastResponse?.playerId === playerId,
      )?.message ??
      state.duo.invitations.received.find(
        ({ timestamp, playerId }) =>
          state.duo.lastResponse?.timestamp === timestamp &&
          state.duo.lastResponse?.playerId === playerId,
      )?.message,

    // Answers to any conversation questions given by the friend
    ...Object.entries(state.visit.visitSession?.answers ?? {}).reduce(
      (acc, [answerVar, answer]) => {
        acc[`friend.${answerVar}`] = answer.value;
        return acc;
      },
      {} as Record<`friend.${string}`, string>,
    ),
  };
  for (const token in substitutions) {
    script = script.replace(
      new RegExp(`{${token}}`, 'g'),
      substitutions[token]!,
    );
  }

  return script;
}

/**
 * Get valid dialogs.
 * @param state
 * @param scene
 * @param species
 * @param petState
 * @param answers
 * @returns
 */
export function getValidDialogs(
  state: State['user'],
  scene: DialogScene,
  species: PetSpeciesID,
  petState: PetState,
  answers: Record<string, (typeof state.conversation.answers)[string]>,
) {
  let minTimesSeen: number;
  const dialogs = Object.values(getDialogs()).filter((dialog) => {
    if (!dialog.scenes.some((x) => x === scene)) {
      return false;
    }

    if (dialog.pet_id !== species) {
      return false;
    }

    if (dialog.pet_states && !dialog.pet_states.includes(petState)) {
      return false;
    }

    if (dialog.req_answers?.some((answerKey) => !answers[answerKey])) {
      return false;
    }

    const timesSeen = getTimesSeen(state, dialog.id);
    if (minTimesSeen === undefined || timesSeen < minTimesSeen) {
      minTimesSeen = timesSeen;
    }

    return true;
  });

  return dialogs.filter(({ id }) => getTimesSeen(state, id) === minTimesSeen);
}

export const getLocaleSuffix = (locale: string) =>
  locale === 'en' ? 'en' : 'jp';

/**
 * Get script text.
 * @param state
 * @param scriptId
 * @param locale
 * @param isFriend
 */
export function getScriptQuestion(
  state: State['user'],
  scriptId: DescriptiveScript['id'],
  locale: string,
  isFriend: boolean,
) {
  const descriptive = getScripts()[scriptId] as DescriptiveScript;

  return substituteScriptTokens(
    descriptive[`question_${getLocaleSuffix(locale)}`],
    state,
    isFriend,
  );
}

export function getScriptText(
  state: State['user'],
  scriptId: Script['id'],
  locale: string,
  isFriend: boolean,
) {
  return substituteScriptTokens(
    getScripts()[scriptId][`text_${getLocaleSuffix(locale)}`],
    state,
    isFriend,
  );
}

const dialogs: Record<DialogID, DialogData> = {};
function appendDialogs(species: PetSpeciesID) {
  const conversationAssets = getPetConversationAssetPaths(
    species,
    features(StateObserver.getState().user).newPetDialogs,
  );
  const dialogData = petSpeciesAssets[species].assetsByID[
    conversationAssets.dialogs
  ] as Dialogs;
  appendDialogData(dialogData);
}

export function appendDialogData(dialogData: Dialogs | undefined) {
  // Dialog data parser
  dialogData?.forEach((data) => {
    dialogs[data.id as DialogID] = {
      id: data.id as DialogID,
      pet_id: data.pet_id,
      scenes: data.scenes,
      pet_states: data.pet_states,
      script_id: data.script_id as ScriptID,
      req_answers: data.req_answers,
      pet_level: data.pet_level,
    };
  });
}

/**
 * Get loaded dialogs.
 * @returns
 */
export function getDialogs() {
  return dialogs;
}

const scripts: Record<ScriptID, Script> = {};
function appendScripts(species: PetSpeciesID) {
  const conversationAssets = getPetConversationAssetPaths(
    species,
    features(StateObserver.getState().user).newPetDialogs,
  );
  const scriptsData = petSpeciesAssets[species].assetsByID[
    conversationAssets.scripts
  ] as Scripts;
  appendScriptsData(scriptsData);
}

export function appendScriptsData(scriptsData: Scripts | undefined) {
  // Parse scripts
  scriptsData?.forEach((data) => {
    const type = data.type;

    switch (type) {
      case 'descriptive':
        {
          const descriptive: DescriptiveScript = data as DescriptiveScript;

          scripts[descriptive.id] = {
            id: descriptive.id,
            type,
            animation: descriptive.animation,
            expression: descriptive.expression,
            case: descriptive.case,
            move_to: descriptive.move_to,
            text_jp: descriptive.text_jp,
            text_en: descriptive.text_en,
            answer_var: descriptive.answer_var,
            question_jp: descriptive.question_jp,
            question_en: descriptive.question_en,
            max_len: descriptive.max_len,
            next_script_id: descriptive.next_script_id,
            next_script_id_1: descriptive.next_script_id_1,
            next_script_id_2: descriptive.next_script_id_2,
            next_script_id_3: descriptive.next_script_id_3,
          };
        }

        break;
      case 'selectional':
        {
          const selectional: SelectionalScript = data as SelectionalScript;

          scripts[selectional.id] = {
            id: selectional.id,
            type,
            case: selectional.case,
            animation: selectional.animation,
            expression: selectional.expression,
            move_to: selectional.move_to,
            answer_var: selectional.answer_var,
            text_jp: selectional.text_jp,
            text_en: selectional.text_en,
            option_1_jp: selectional.option_1_jp,
            option_1_en: selectional.option_1_en,
            option_2_jp: selectional.option_2_jp,
            option_2_en: selectional.option_2_en,
            option_3_jp: selectional.option_3_jp,
            option_3_en: selectional.option_3_en,
            next_script_id: selectional.next_script_id,
            next_script_id_1: selectional.next_script_id_1,
            next_script_id_2: selectional.next_script_id_2,
            next_script_id_3: selectional.next_script_id_3,
          };
        }
        break;
      case 'conversational':
        {
          const conversation: ConversationalScript =
            data as ConversationalScript;

          scripts[conversation.id] = {
            id: conversation.id,
            type,
            text_jp: conversation.text_jp,
            text_en: conversation.text_en,
            animation: conversation.animation,
            move_to: conversation.move_to,
            expression: conversation.expression,
            case: conversation.case,
            next_script_id: conversation.next_script_id,
            next_script_id_1: conversation.next_script_id_1,
            next_script_id_2: conversation.next_script_id_2,
            next_script_id_3: conversation.next_script_id_3,
          };
        }
        break;
      default:
        assertNever(type);
    }
  });
}

/**
 * Get loaded scripts.
 * @returns
 */
export function getScripts() {
  return scripts;
}

export function appendToConversationData(species: PetSpeciesID) {
  appendDialogs(species);
  appendScripts(species);

  // Add shared want_to_add_friend script and per-species dialog entry
  appendScriptsData([
    {
      id: 'want_to_add_friend_01',
      type: 'conversational' as const,
      move_to: 'c',
      text_en:
        'I wish I had more friends to play with... Hey! Do you think you could find me a new friend?',
      text_jp: 'もっとお友だちほしいな…\n新しいお友だち、見つけてくれる？',
    },
  ]);
  appendDialogData([
    {
      id: `want_to_add_friend_${species}`,
      pet_id: species,
      scenes: ['normal_chat'],
      pet_states: ['want_to_add_friend'],
      script_id: 'want_to_add_friend_01',
    },
  ]);
}
